import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { projects } from '../data/projects';

function FadeIn({ children, delay = 0 }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { el.classList.add('visible'); obs.disconnect(); }
    }, { threshold: 0.06 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return <div ref={ref} className="fade-in" style={{ transitionDelay: `${delay}ms` }}>{children}</div>;
}

function StatusBadge({ status }) {
  const map = {
    'Completed': { dot: '#22C55E', label: 'Completed' },
    'In Progress': { dot: 'var(--accent)', label: 'In Progress' },
    'Interrupted': { dot: '#6B7280', label: 'Interrupted' }
  };
  const { dot, label } = map[status] || { dot: '#6B7280', label: status };
  return (
    <span className="status-badge">
      <span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', background: dot, flexShrink: 0 }} />
      {label}
    </span>
  );
}

function ProjectCard({ p, delay = 0 }) {
  const navigate = useNavigate();
  return (
    <FadeIn delay={delay}>
      <div className="project-card" onClick={() => navigate(`/projects/${p.id}`)} style={{ cursor: 'pointer' }}>
        <div className="project-card-top">
          <StatusBadge status={p.status || p.state} />
          {p.visibility && <span className="project-visibility">{p.visibility}</span>}
        </div>
        <h3 className="project-title">{p.title || p.name}</h3>
        <p className="project-desc">{p.description}</p>
        <div className="project-footer">
          <div className="tag-row">
            {(p.technologies || []).slice(0, 5).map(t => <span key={t} className="tag">{t}</span>)}
            {(p.technologies || []).length > 5 && <span className="tag">+{(p.technologies || []).length - 5}</span>}
          </div>
          {p.categories && (
            <div className="tag-row" style={{ marginTop: 6 }}>
              {p.categories.slice(0, 3).map(c => <span key={c} className="tag tag--dim">{c}</span>)}
            </div>
          )}
          <div className="project-meta-row">
            <span className="project-date">{p.dateRange || p.date}</span>
            {p.githubLink && <a href={p.githubLink} target="_blank" rel="noreferrer" className="project-link" onClick={e => e.stopPropagation()}>View GitHub ↗</a>}
          </div>
        </div>
      </div>
    </FadeIn>
  );
}

function TimelineEntry({ p, index }) {
  const navigate = useNavigate();
  const isLeft = index % 2 === 0;
  return (
    <FadeIn delay={index * 40}>
      <div className={`tl-entry ${isLeft ? 'tl-entry--left' : 'tl-entry--right'}`} onClick={() => navigate(`/projects/${p.id}`)} style={{ cursor: 'pointer' }}>
        <div className="tl-dot-col">
          <div className="tl-line" />
          <div className="tl-dot" />
        </div>
        <div className="tl-card">
          <div className="project-card-top">
            <StatusBadge status={p.status || p.state} />
            <span className="project-date" style={{ marginLeft: 'auto' }}>{p.dateRange || p.date}</span>
          </div>
          <h3 className="project-title" style={{ fontSize: 16, marginTop: 8 }}>{p.title || p.name}</h3>
          <p className="project-desc" style={{ fontSize: 13, WebkitLineClamp: 3 }}>{p.description}</p>
          <div className="tag-row" style={{ marginTop: 10 }}>
            {(p.technologies || []).slice(0, 4).map(t => <span key={t} className="tag">{t}</span>)}
          </div>
          {p.githubLink && <a href={p.githubLink} target="_blank" rel="noreferrer" className="project-link" style={{ display: 'inline-block', marginTop: 10 }} onClick={e => e.stopPropagation()}>View GitHub ↗</a>}
        </div>
      </div>
    </FadeIn>
  );
}

const FILTER_CATEGORIES = [
  'AI Agents', 'Machine Learning', 'Deep Learning', 'Computer Vision',
  'NLP', 'Robotics', 'Simulation', 'Reinforcement Learning',
  'Hackathon', 'Research', 'Dashboard', 'Mobile',
];

export default function Projects() {
  const [view, setView] = useState('grid');
  const [dismissed, setDismissed] = useState(false);
  const [activeFilters, setActiveFilters] = useState(new Set());

  return (
    <>
    <Helmet>
      <title>Projects | Salvatore Lo Sardo</title>
      <meta name="description" content="AI and machine learning projects by Salvatore Lo Sardo — computer vision, agentic AI, RAG pipelines, robotics, and more." />
      <meta property="og:title" content="Projects | Salvatore Lo Sardo" />
      <meta property="og:description" content="AI and machine learning projects — computer vision, agentic AI, RAG pipelines, robotics, and more." />
      <meta property="og:url" content="https://www.salvatore.losardo.eu/projects" />
      <link rel="canonical" href="https://www.salvatore.losardo.eu/projects" />
    </Helmet>
    <div className="page">
      <div className="container">
        <FadeIn>
          <div className="page-hero">
            <h1 className="page-hero-title">Projects</h1>
            <p className="page-hero-sub">A selection of my personal and professional projects, highlighting a range of technologies and problem-solving approaches.</p>
          </div>
        </FadeIn>

        {!dismissed && (
          <FadeIn>
            <div className="info-banner">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
              <p>Some projects are <strong>works in progress</strong>, developed alongside academic studies. While not all are fully polished, they demonstrate valuable practical experience and technical curiosity.</p>
              <button className="banner-close" onClick={() => setDismissed(true)}>✕</button>
            </div>
          </FadeIn>
        )}

        <FadeIn>
          <div className="view-toggle" style={{ marginBottom: 28 }}>
            <button className={`view-btn ${view === 'grid' ? 'view-btn--active' : ''}`} onClick={() => setView('grid')}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="3" y="14" width="7" height="7" /><rect x="14" y="14" width="7" height="7" /></svg>
              Grid
            </button>
            <button className={`view-btn ${view === 'timeline' ? 'view-btn--active' : ''}`} onClick={() => setView('timeline')}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="18" x2="21" y2="18" /></svg>
              Timeline
            </button>
          </div>
        </FadeIn>

        <FadeIn>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 28 }}>
            <button
              className={`filter-chip ${activeFilters.size === 0 ? 'filter-chip--active' : ''}`}
              onClick={() => setActiveFilters(new Set())}
            >All</button>
            {FILTER_CATEGORIES.map(cat => (
              <button
                key={cat}
                className={`filter-chip ${activeFilters.has(cat) ? 'filter-chip--active' : ''}`}
                onClick={() => setActiveFilters(prev => {
                  const next = new Set(prev);
                  next.has(cat) ? next.delete(cat) : next.add(cat);
                  return next;
                })}
              >{cat}</button>
            ))}
          </div>
        </FadeIn>

        {(() => {
          const filtered = activeFilters.size === 0
            ? projects
            : projects.filter(p => p.categories?.some(c => activeFilters.has(c)));
          return view === 'grid' ? (
            <div className="projects-grid">
              {filtered.map((p, i) => <ProjectCard key={p.id || i} p={p} delay={i * 40} />)}
            </div>
          ) : (
            <div className="timeline-container">
              {filtered.map((p, i) => <TimelineEntry key={p.id || i} p={p} index={i} />)}
            </div>
          );
        })()}
      </div>
    </div>
    </>
  );
}